import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { MainLayout } from '../components/Layout';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Search, Package, AlertTriangle, ArrowRight, Link2, Eye, FileText, Loader2, Plus, Pencil, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import Pagination from '../components/Pagination';
import { CURRENCY_SYMBOL_MAP } from '../constants/currencies';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ─── Part Form Modal ──────────────────────────────────────────────────────────
function PartFormModal({ mode, part, onClose, onSuccess }) {
  const isEdit = mode === 'edit';

  const [formData, setFormData] = useState({
    part_number: part?.part_number || '',
    part_name: part?.name || '',
    description: part?.description || '',
    purchase_price: part?.sap_purchase_price || '',
  });
  const [saving, setSaving] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.part_number.trim() || !formData.part_name.trim() || !formData.description.trim()) {
      toast.error('All fields are required');
      return;
    }
    if (!formData.purchase_price || parseFloat(formData.purchase_price) <= 0) {
      toast.error('Purchase price must be greater than 0');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        part_number: formData.part_number.trim().toUpperCase(),
        part_name: formData.part_name.trim(),
        description: formData.description.trim(),
        purchase_price: parseFloat(formData.purchase_price),
      };

      if (isEdit) {
        await axios.put(`${API}/parts/${part.part_number}`, payload);
        toast.success('Part updated successfully');
        onSuccess();
      } else {
        const response = await axios.post(`${API}/parts`, payload);
        toast.success('Part created successfully');
        onSuccess(response.data);
      }
    } catch (error) {
      const msg = error?.response?.data?.detail || (isEdit ? 'Failed to update part' : 'Failed to create part');
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      data-testid={isEdit ? 'edit-part-modal' : 'create-part-modal'}
    >
      <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-xl shadow-2xl w-full max-w-lg mx-4">
        {/* Modal Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-200 dark:border-zinc-800">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-[#C3002F]/10 rounded">
              {isEdit ? <Pencil className="w-5 h-5 text-[#C3002F]" /> : <Plus className="w-5 h-5 text-[#C3002F]" />}
            </div>
            <div>
              <h2 className="text-lg font-semibold text-zinc-900 dark:text-white">
                {isEdit ? 'Edit Part' : 'Create New Part'}
              </h2>
              <p className="text-xs text-zinc-500">
                {isEdit ? `Editing ${part.part_number}` : 'Add a new part to the catalog'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded hover:bg-zinc-100 dark:hover:bg-zinc-800 text-zinc-500 hover:text-zinc-700 dark:hover:text-zinc-300 transition-colors"
            data-testid="close-modal-btn"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Modal Body */}
        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
          {/* Part Number */}
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-zinc-700 dark:text-zinc-300">
              Part Number <span className="text-[#C3002F]">*</span>
            </label>
            <Input
              name="part_number"
              value={formData.part_number}
              onChange={handleChange}
              placeholder="e.g. NSN-FLT-0001"
              disabled={isEdit}
              className={`font-mono ${isEdit ? 'opacity-60 cursor-not-allowed' : ''}`}
              data-testid="part-number-input"
            />
            {!isEdit && (
              <p className="text-xs text-zinc-500">
                Family is inferred from prefix: ENG, BRK, SUS, ELE, BDY
              </p>
            )}
          </div>

          {/* Part Name */}
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-zinc-700 dark:text-zinc-300">
              Part Name <span className="text-[#C3002F]">*</span>
            </label>
            <Input
              name="part_name"
              value={formData.part_name}
              onChange={handleChange}
              placeholder="e.g. Brake Filter"
              data-testid="part-name-input"
            />
          </div>

          {/* Description */}
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-zinc-700 dark:text-zinc-300">
              Description <span className="text-[#C3002F]">*</span>
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              placeholder="Short description of the part"
              rows={3}
              className="w-full px-3 py-2 text-sm rounded-md border border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-900 text-zinc-900 dark:text-white placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-[#C3002F]/30 focus:border-[#C3002F] resize-none"
              data-testid="part-description-input"
            />
          </div>

          {/* Purchase Price */}
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-zinc-700 dark:text-zinc-300">
              SAP Purchase Price <span className="text-[#C3002F]">*</span>
            </label>
            <Input
              name="purchase_price"
              type="number"
              min="0.01"
              step="0.01"
              value={formData.purchase_price}
              onChange={handleChange}
              placeholder="0.00"
              className="font-mono"
              data-testid="purchase-price-input"
            />
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="border-zinc-300 dark:border-zinc-700"
              data-testid="cancel-btn"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={saving}
              className="bg-[#C3002F] hover:bg-[#9f0026] text-white flex items-center gap-2"
              data-testid="save-part-btn"
            >
              {saving ? (
                <><Loader2 className="w-4 h-4 animate-spin" />{isEdit ? 'Updating…' : 'Creating…'}</>
              ) : (
                <>{isEdit ? <><Pencil className="w-4 h-4" />Update Part</> : <><Plus className="w-4 h-4" />Create Part</>}</>
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function PartsSearchPage() {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [existingCards, setExistingCards] = useState({});
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [searched, setSearched] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const navigate = useNavigate();

  // Modal state
  const [modal, setModal] = useState(null); // null | { mode: 'create' } | { mode: 'edit', part: Part }

  // Load all parts on initial mount
  useEffect(() => {
    const loadAllParts = async () => {
      setInitialLoading(true);
      setSearched(true);
      try {
        const response = await axios.get(`${API}/parts/search?q=`);
        setResults(response.data);

        if (response.data.length > 0) {
          const cardChecks = {};
          await Promise.all(
            response.data.map(async (part) => {
              try {
                const checkResponse = await axios.get(`${API}/cogs-cards/check-part/${part.part_number}`);
                if (checkResponse.data.exists) {
                  cardChecks[part.part_number] = checkResponse.data;
                }
              } catch (err) {
                // Ignore errors
              }
            })
          );
          setExistingCards(cardChecks);
        }
      } catch (error) {
        console.error('Failed to load parts', error);
      } finally {
        setInitialLoading(false);
      }
    };

    loadAllParts();
  }, []);

  const handleSearch = async (e) => {
    e?.preventDefault();
    if (!query.trim()) return;

    setLoading(true);
    setSearched(true);
    setCurrentPage(1);

    try {
      const response = await axios.get(`${API}/parts/search?q=${encodeURIComponent(query)}`);
      setResults(response.data);

      if (response.data.length === 0) {
        toast.info('No parts found matching your query');
      } else {
        const cardChecks = {};
        await Promise.all(
          response.data.map(async (part) => {
            try {
              const checkResponse = await axios.get(`${API}/cogs-cards/check-part/${part.part_number}`);
              if (checkResponse.data.exists) {
                cardChecks[part.part_number] = checkResponse.data;
              }
            } catch (err) {
              // Ignore errors
            }
          })
        );
        setExistingCards(cardChecks);
      }
    } catch (error) {
      toast.error('Search failed');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleModalSuccess = (newPart) => {
    setModal(null);
    if (newPart) {
      setResults((prev) => [newPart, ...prev]);
      setCurrentPage(1);
    } else if (searched && query.trim()) {
      handleSearch();
    }
  };

  // Pagination logic
  const totalPages = Math.ceil(results.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedResults = results.slice(startIndex, endIndex);

  const handleCreateCostCard = (part) => {
    navigate(`/cost-cards/create?part=${part.part_number}`);
  };

  const handleViewCostCard = (cardId) => {
    navigate(`/cost-cards/${cardId}`);
  };

  return (
    <MainLayout>
      <div className="p-8 space-y-8" data-testid="parts-search-page">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-4xl font-bold text-zinc-900 dark:text-white tracking-tight">Part Search</h1>
            <p className="text-zinc-500 dark:text-zinc-400 mt-1">Search for parts by part number or name to create cost cards</p>
          </div>
          <Button
            onClick={() => setModal({ mode: 'create' })}
            className="bg-[#C3002F] hover:bg-[#9f0026] text-white flex items-center gap-2"
            data-testid="create-part-btn"
          >
            <Plus className="w-4 h-4" />
            New Part
          </Button>
        </div>

        {/* Search Bar */}
        <form onSubmit={handleSearch} className="relative max-w-3xl">
          <div className="gap-x-2 flex items-center w-full relative rounded-lg overflow-hidden">
            <div className={`w-full`}>
              <Input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Enter part number or name (e.g., NSN-FLT-0001 or Brake Filter)"
                className="h-14 w-full text-lg bg-white dark:bg-zinc-900 border-zinc-300 dark:border-zinc-800 "
                data-testid="part-search-input"
              />
            </div>
            <Button
              type="submit"
              disabled={loading}
              className="h-14 bg-[#C3002F] hover:bg-[#9f0026] text-white flex items-center gap-2"
              data-testid="search-btn"
            >
              {loading ? <><Loader2 className="w-4 h-4 animate-spin" />Searching...</> : 'Search'}
            </Button>
          </div>
          <p className="text-xs text-zinc-500 mt-2">
            Tip: Search by full or partial part number or name. Try &ldquo;NSN-FLT&rdquo; or &ldquo;Filter&rdquo; to see all filter parts.
          </p>
        </form>

        {/* Content area */}
        <div className="space-y-4">
          {(initialLoading || loading) ? (
            <div className="grid gap-4">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 p-5 rounded-lg animate-pulse">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-zinc-200 dark:bg-zinc-800 rounded" />
                    <div className="flex-1 space-y-3">
                      <div className="h-5 bg-zinc-200 dark:bg-zinc-800 rounded w-40" />
                      <div className="h-4 bg-zinc-200 dark:bg-zinc-800 rounded w-64" />
                      <div className="h-3 bg-zinc-200 dark:bg-zinc-800 rounded w-48" />
                      <div className="flex gap-6 mt-4">
                        {[...Array(4)].map((_, j) => (
                          <div key={j} className="space-y-1">
                            <div className="h-3 bg-zinc-200 dark:bg-zinc-800 rounded w-16" />
                            <div className="h-4 bg-zinc-200 dark:bg-zinc-800 rounded w-20" />
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="h-9 bg-zinc-200 dark:bg-zinc-800 rounded w-36" />
                  </div>
                </div>
              ))}
            </div>
          ) : searched ? (
            <>
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-zinc-900 dark:text-white">
                  {results.length} {results.length === 1 ? 'Result' : 'Results'} Found
                </h2>
              </div>

              {results.length > 0 ? (
                <div className="grid gap-4">
                  {paginatedResults.map((part) => (
                    <div
                      key={part.id}
                      className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 p-5 rounded-lg hover:border-zinc-300 dark:hover:border-zinc-700 hover:shadow-md transition-all duration-200"
                      data-testid={`part-result-${part.part_number}`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-4">
                          <div className="p-3 bg-zinc-100 dark:bg-zinc-800 rounded">
                            <Package className="w-6 h-6 text-zinc-500 dark:text-zinc-400" />
                          </div>
                          <div>
                            <div className="flex items-center gap-3">
                              <h3 className="text-lg font-mono font-semibold text-zinc-900 dark:text-white">{part.part_number}</h3>
                              {part.has_super_seeded && (
                                <span className="flex items-center gap-1 text-xs px-2 py-0.5 bg-cyan-100 dark:bg-cyan-500/20 text-cyan-700 dark:text-cyan-400 border border-cyan-200 dark:border-cyan-500/30 rounded">
                                  <Link2 className="w-3 h-3" />
                                  Super Seeded
                                </span>
                              )}
                            </div>
                            <p className="text-zinc-700 dark:text-zinc-300 mt-1">{part.name}</p>
                            <p className="text-sm text-zinc-500 mt-1">{part.description}</p>

                            <div className="flex items-center gap-6 mt-4">
                              <div>
                                <p className="text-xs text-zinc-500 uppercase tracking-wider">Family</p>
                                <p className="text-sm text-zinc-700 dark:text-zinc-300">{part.family_name}</p>
                              </div>
                              <div>
                                <p className="text-xs text-zinc-500 uppercase tracking-wider">SAP Price</p>
                                <p className="text-sm font-mono text-emerald-600 dark:text-emerald-400">
                                  {/* {CURRENCY_SYMBOL_MAP[part.currency] ?? part.currency} */}
                                  ₹
                                  {part.sap_purchase_price.toLocaleString()}
                                </p>
                              </div>
                              <div>
                                <p className="text-xs text-zinc-500 uppercase tracking-wider">Supplier</p>
                                <p className="text-sm text-zinc-700 dark:text-zinc-300">{part.supplier}</p>
                              </div>
                              <div>
                                <p className="text-xs text-zinc-500 uppercase tracking-wider">Lead Time</p>
                                <p className="text-sm text-zinc-700 dark:text-zinc-300">{part.lead_time_days} days</p>
                              </div>
                            </div>

                            {/* Alerts */}
                            {(part.has_super_seeded || part.family_reference_dealer_price) && (
                              <div className="mt-4 space-y-2">
                                {part.has_super_seeded && (
                                  <div className="flex items-start gap-2 p-3 bg-cyan-50 dark:bg-cyan-500/10 border border-cyan-200 dark:border-cyan-500/20 rounded text-sm">
                                    <AlertTriangle className="w-4 h-4 text-cyan-600 dark:text-cyan-400 mt-0.5 flex-shrink-0" />
                                    <div>
                                      <p className="text-cyan-700 dark:text-cyan-400 font-medium">Super Seeded Part Available</p>
                                      <p className="text-zinc-600 dark:text-zinc-400">
                                        This part has been super seeded by <span className="font-mono text-cyan-600 dark:text-cyan-300">{part.super_seeded_part_number}</span>.
                                        Consider adjusting price by {part.super_seeded_adjustment}%.
                                      </p>
                                    </div>
                                  </div>
                                )}
                                {part.family_reference_dealer_price && (
                                  <div className="flex items-start gap-2 p-3 bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/20 rounded text-sm">
                                    <AlertTriangle className="w-4 h-4 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
                                    <div>
                                      <p className="text-amber-700 dark:text-amber-400 font-medium">Family Reference Price Available</p>
                                      <p className="text-zinc-600 dark:text-zinc-400">
                                        Other parts in this family are priced at {CURRENCY_SYMBOL_MAP[part.currency] ?? part.currency}{part.family_reference_dealer_price.toLocaleString()} dealer price.
                                      </p>
                                    </div>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="flex flex-col items-end gap-2 shrink-0 ml-4">
                          {/* Edit Part button — always visible */}
                          <Button
                            onClick={() => setModal({ mode: 'edit', part })}
                            variant="outline"
                            size="sm"
                            className="border-zinc-300 dark:border-zinc-700 text-zinc-600 dark:text-zinc-400 hover:bg-zinc-100 dark:hover:bg-zinc-800 flex items-center gap-1.5"
                            data-testid={`edit-part-btn-${part.part_number}`}
                          >
                            <Pencil className="w-3.5 h-3.5" />
                            Edit Part
                          </Button>

                          {existingCards[part.part_number] ? (
                            <>
                              <span className={`text-xs px-2 py-1 rounded ${existingCards[part.part_number].status === 'approved' ? 'bg-emerald-100 dark:bg-emerald-500/20 text-emerald-700 dark:text-emerald-400' :
                                existingCards[part.part_number].status === 'pending' ? 'bg-amber-100 dark:bg-amber-500/20 text-amber-700 dark:text-amber-400' :
                                  existingCards[part.part_number].status === 'rejected' ? 'bg-red-100 dark:bg-red-500/20 text-red-700 dark:text-red-400' :
                                    'bg-blue-100 dark:bg-blue-500/20 text-blue-700 dark:text-blue-400'
                                }`}>
                                {existingCards[part.part_number].status}
                              </span>
                              <Button
                                onClick={() => handleViewCostCard(existingCards[part.part_number].card_id)}
                                variant="outline"
                                className="border-zinc-300 dark:border-zinc-700 text-zinc-700 dark:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-zinc-800 flex items-center gap-2"
                                data-testid={`view-card-btn-${part.part_number}`}
                              >
                                <Eye className="w-4 h-4" />
                                View COGS Card
                              </Button>
                            </>
                          ) : (
                            <Button
                              onClick={() => handleCreateCostCard(part)}
                              className="bg-[#C3002F] hover:bg-[#9f0026] text-white flex items-center gap-2"
                              data-testid={`create-card-btn-${part.part_number}`}
                            >
                              <FileText className="w-4 h-4" />
                              Create COGS Card
                              <ArrowRight className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-16 text-center">
                  <Package className="w-16 h-16 text-zinc-300 dark:text-zinc-700 mb-4" />
                  <h3 className="text-xl font-semibold text-zinc-600 dark:text-zinc-400">No Parts Found</h3>
                  <p className="text-zinc-500 mt-2">Try a different part number or check for typos</p>
                  <Button
                    onClick={() => setModal({ mode: 'create' })}
                    className="mt-4 bg-[#C3002F] hover:bg-[#9f0026] text-white flex items-center gap-2"
                    data-testid="create-part-empty-btn"
                  >
                    <Plus className="w-4 h-4" />
                    Create This Part
                  </Button>
                </div>
              )}

              {/* Pagination */}
              {results.length > 0 && (
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={setCurrentPage}
                  totalItems={results.length}
                  itemsPerPage={itemsPerPage}
                  className="mt-6"
                />
              )}
            </>
          ) : (
            <div className="flex flex-col items-center justify-center py-24 text-center">
              <div className="p-6 bg-zinc-100 dark:bg-zinc-900 rounded-full mb-6">
                <Search className="w-12 h-12 text-zinc-400 dark:text-zinc-600" />
              </div>
              <h3 className="text-xl font-semibold text-zinc-600 dark:text-zinc-400">Search for Parts</h3>
              <p className="text-zinc-500 mt-2 max-w-md">
                Enter a part number to fetch SAP data and create cost cards with automatic pricing calculations.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Create / Edit Modal */}
      {modal && (
        <PartFormModal
          mode={modal.mode}
          part={modal.part}
          onClose={() => setModal(null)}
          onSuccess={handleModalSuccess}
        />
      )}
    </MainLayout>
  );
}